/**
 * Accepting an image from the three entry points the app supports:
 * a file picker, a drag-and-drop, and a clipboard paste.
 *
 * All three surface their payload as a `DataTransfer`-shaped object, so the
 * extraction logic is shared and kept free of DOM event handling.
 */

export const SUPPORTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/avif',
] as const

/** Extensions used as a fallback when a platform reports an empty MIME type. */
const SUPPORTED_EXTENSIONS = /\.(png|jpe?g|webp|gif|bmp|avif)$/i

export const MAX_IMAGE_BYTES = 64 * 1024 * 1024

export interface FileLike {
  readonly name: string
  readonly type: string
  readonly size: number
}

export interface DataTransferLike {
  readonly files?: ArrayLike<FileLike> | null
  readonly items?: ArrayLike<DataTransferItemLike> | null
}

export interface DataTransferItemLike {
  readonly kind: string
  readonly type: string
  getAsFile(): FileLike | null
}

export function isSupportedImage(file: FileLike): boolean {
  if (file.type) {
    return (SUPPORTED_IMAGE_TYPES as readonly string[]).includes(file.type.toLowerCase())
  }
  // Some platforms (notably Linux drag sources) report an empty type.
  return SUPPORTED_EXTENSIONS.test(file.name)
}

/**
 * Pulls the first usable image out of a drop or paste payload.
 *
 * `items` is preferred over `files` because a clipboard paste of a screenshot
 * populates only `items`, while a file drop populates both.
 */
export function imageFromDataTransfer(data: DataTransferLike | null | undefined): FileLike | null {
  if (!data) return null

  const items = data.items
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.kind !== 'file') continue
      const file = item.getAsFile()
      if (file && isSupportedImage(file)) return file
    }
  }

  const files = data.files
  if (files) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (isSupportedImage(file)) return file
    }
  }

  return null
}

export type IntakeRejection =
  | { ok: false; reason: 'unsupported-type'; message: string }
  | { ok: false; reason: 'too-large'; message: string }

export type IntakeResult<T extends FileLike> = { ok: true; file: T } | IntakeRejection

/** Validates a chosen file before the expensive decode step. */
export function validateImageFile<T extends FileLike>(file: T): IntakeResult<T> {
  if (!isSupportedImage(file)) {
    return {
      ok: false,
      reason: 'unsupported-type',
      message: `"${file.name}" is not a supported image. Use PNG, JPEG, WebP, GIF, BMP, or AVIF.`,
    }
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return {
      ok: false,
      reason: 'too-large',
      message: `"${file.name}" is ${formatBytes(file.size)}, larger than the ${formatBytes(
        MAX_IMAGE_BYTES,
      )} limit.`,
    }
  }
  return { ok: true, file }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}
